 
'use client';
import {
    CHAT_INSTANCE_INIT_THROW_ID,
    CHAT_INSTANCE_SEND_THROW_ID,
    CHAT_INSTANCE_STREAM_HANDLER_THROW_ID,
    CHAT_INSTANCE_STREAM_THROW_ID,
    CHAT_INSTANCE_THREAD_CREATE_THROW_ID,
} from '@korvo/core/lib/chat-client-error-message';
import { ERR_CHAT_500, ERR_INTERNAL_500, PORTAL_CONFIG_MISSING } from '@korvo/core/lib/error-codes';
import { KorvoError } from '@korvo/core/lib/korvo-error';
import { normalizeError } from '@korvo/core/lib/normalize-error';
import type { ChatRole } from '@korvo/core/shared/types/chat';
import { MessageNormalizer, type RawMessage } from '@korvo/portal-sdk/message-normalizer';
import { EventEmitter } from 'events';

import { looksLikeJsonObjectPayload, parseStreamChunk, type ToolCallPayload } from './messages-api-stream-parse';
import { getPortalApiBase, ThreadsClient } from './portalClient';
import { applyToolUpdateToMessage, composeToolExecutionMessage } from './toolMessageUtils';
import type { ChatError, ChatInstanceConfig, ChatInstanceState, ChatMessage, ChatSession, ToolCall } from './types';

const ASSISTANT_DUPLICATE_WINDOW_MS = 2 * 1000;
const STREAM_RECENT_THRESHOLD_MS = 5 * 1000;
const HEARTBEAT_INTERVAL_MS = 30 * 1000;
type PortalMessagePayload = {
    id: string;
    role: string;
    content: string;
    timestamp?: number;
    files?: File[];
    toolCalls?: ToolCallPayload[];
};
function safePortalBase(apiBase?: string, pathname?: string): string {
    const portalBaseResolver = getPortalApiBase as (apiBase?: string, pathname?: string) => string;
    const base: unknown = portalBaseResolver(apiBase, pathname);
    if (typeof base !== 'string') {
        throw new KorvoError(PORTAL_CONFIG_MISSING, 1, { message: 'Invalid portal api base' });
    }
    return base;
}
export class ChatInstance extends EventEmitter {
    private instanceId: string;
    private config: ChatInstanceConfig;
    private state: ChatInstanceState;
    private threadsClient: ThreadsClient | null = null;
    private eventSource: EventSource | null = null;
    private currentThreadId: string | null = null;
    private seenToolCallIds: Set<string> = new Set();
    private isDestroyed: boolean = false;
    private isPaused: boolean = false;
    private heartbeatInterval: NodeJS.Timeout | null = null;
    private lastActivity: number = Date.now();
    constructor(instanceId: string, config: ChatInstanceConfig) {
        super();
        this.instanceId = instanceId;
        this.config = config;
        this.state = {
            messages: [],
            isLoading: false,
            isStreaming: false,
            error: null,
            session: null,
            isPaused: false,
            isDestroyed: false
        };
        // Initialize Portal SDK client
        try {
            const apiEndpoint = this.config.provider.apiEndpoint;
            const providerApiBase = safePortalBase(apiEndpoint, typeof window !== 'undefined' ? window.location.pathname : undefined);
            this.threadsClient = new ThreadsClient(window.location.origin, providerApiBase);
        }
        catch (err: unknown) {
            const errNormalized = normalizeError(err);
            console.error('Failed to initialize ThreadsClient:', errNormalized);
            this.threadsClient = null;
        }
        void this.initialize();
        this.startHeartbeat();
    }
    private async initialize() {
        try {
            // Initialize with empty session - will be populated by ChatListContext
            const now = Date.now();
            this.state.session = {
                id: this.config.sessionId,
                title: `Chat ${Date.now()}`,
                messages: [],
                createdAt: now,
                updatedAt: now,
                metadata: {}
            };
            this.emit('state:changed', this.getState());
        }
        catch (err: unknown) {
            const errNormalized = normalizeError(err);
            console.error(`[ChatInstance ${this.instanceId}] Failed to initialize:`, errNormalized);
            this.setKorvoChatError(ERR_INTERNAL_500, CHAT_INSTANCE_INIT_THROW_ID, 'Failed to initialize chat instance');
        }
    }
    getState(): ChatInstanceState {
        return { ...this.state };
    }
    async loadSession(session: ChatSession) {
        try {
            this.state.session = session;
            // If session has a threadId, load messages and open stream
            const threadId = session.metadata?.threadId as string | undefined;
            if (threadId) {
                // Load existing messages from server
                try {
                    const messages = await this.threadsClient!.getMessages(threadId) as PortalMessagePayload[];
                    // Convert PortalMessagePayload to RawMessage format for normalization
                    const rawMessages: RawMessage[] = messages.map((msg) => ({
                        id: msg.id,
                        role: msg.role,
                        content: msg.content ?? null,
                        contentSoFar: null,
                        timestamp: msg.timestamp,
                        createdAt: msg.timestamp ? new Date(msg.timestamp) : new Date(),
                        updatedAt: null,
                        toolCalls: msg.toolCalls ? (msg.toolCalls as unknown[]) : null,
                        toolResults: null,
                        tokensIn: null,
                        tokensOut: null,
                        costCents: null,
                        processingTime: null,
                        isQueryMessage: (msg as any).isQueryMessage ?? false,
                        isHelperMessage: (msg as any).isHelperMessage ?? false,
                        ragResults: (msg as any).ragResults ?? undefined,
                        metadata: (msg as any).metadata ?? undefined,
                        files: msg.files ? msg.files.map(f => ({
                            name: f.name,
                            type: f.type,
                            size: f.size,
                        })) : undefined,
                    }));
                    
                    // Normalize messages using MessageNormalizer (preserves query/helper roles)
                    const normalized = MessageNormalizer.normalize(rawMessages);
                    
                    // Deduplicate by message ID and handle assistant message duplicates
                    const seenIds = new Set<string>();
                    const normalizedMessages: ChatMessage[] = [];
                    for (const msg of normalized) {
                        if (seenIds.has(msg.id)) {
                            continue;
                        }
                        seenIds.add(msg.id);
                        
                        // Convert NormalizedMessage to ChatMessage format
                        const chatMessage: ChatMessage = {
                            id: msg.id,
                            role: msg.role as ChatRole,
                            content: msg.content,
                            timestamp: msg.timestamp,
                            files: msg.files,
                            toolCalls: msg.toolCalls,
                            metadata: msg.metadata,
                        };
                        
                        // Preserve agent message flags if present
                        if ((msg as any).isQueryMessage) {
                            (chatMessage as any).isQueryMessage = true;
                        }
                        if ((msg as any).isHelperMessage) {
                            (chatMessage as any).isHelperMessage = true;
                        }
                        if ((msg as any).ragResults) {
                            (chatMessage as any).ragResults = (msg as any).ragResults;
                        }
                        
                        // If multiple assistant messages arrive within a short window (duplicate reload bug),
                        // keep only the most recent one.
                        if (chatMessage.role === 'assistant' && normalizedMessages.length > 0) {
                            const lastIndex = normalizedMessages.length - 1;
                            const lastMessage = normalizedMessages[lastIndex];
                            if (lastMessage.role === 'assistant' &&
                                Math.abs((chatMessage.timestamp || 0) - (lastMessage.timestamp || 0)) <= ASSISTANT_DUPLICATE_WINDOW_MS) {
                                normalizedMessages[lastIndex] = chatMessage;
                                continue;
                            }
                        }
                        normalizedMessages.push(chatMessage);
                    }
                    this.state.messages = normalizedMessages;
                }
                catch (err: unknown) {
                    const errNormalized = normalizeError(err);
                    console.error(`[ChatInstance ${this.instanceId}] Failed to load messages:`, errNormalized);
                }
                // Open stream (no-op, but kept for compatibility)
                this.openStream(threadId as string);
            }
            this.emit('state:changed', this.getState());
        }
        catch (err: unknown) {
            const errNormalized = normalizeError(err);
            console.error(`[ChatInstance ${this.instanceId}] Failed to load session:`, errNormalized);
        }
    }
    getInstanceId(): string {
        return this.instanceId;
    }
    getCurrentThreadId(): string | null {
        return this.currentThreadId;
    }
    getSessionId(): string {
        return this.config.sessionId;
    }
    isActive(): boolean {
        return !this.isDestroyed && !this.isPaused;
    }
    // Public accessors for lifecycle state
    isPausedState(): boolean {
        return this.isPaused;
    }
    isDestroyedState(): boolean {
        return this.isDestroyed;
    }
    async sendMessage(content: string, files: File[] = [], context?: Record<string, unknown>): Promise<void> {
        if (this.isDestroyed || !content.trim() || this.state.isLoading) {
            return;
        }
        const userMessage: ChatMessage = {
            id: `user_${Date.now()}`,
            role: 'user',
            content: content.trim(),
            timestamp: Date.now(),
            files: files.map(f => ({
                name: f.name,
                type: f.type,
                size: f.size
            }))
        };
        this.addMessage(userMessage);
        this.setState({ isLoading: true, isStreaming: true, error: null });
        try {
            // Call onMessageSent hook
            this.config.onMessageSent?.(userMessage);
            // Send via Portal Threads; streaming will arrive on SSE
            const meta: Record<string, unknown> = this.state.session!.metadata || {};
            if (!meta.threadId) {
                const created = await this.threadsClient!.createThread({
                    packageName: 'chat', systemName: 'general-chat', title: this.state.session!.title,
                });
                const threadId = (created as {
                    thread?: {
                        id?: string;
                    };
                }).thread?.id;
                if (!threadId) {
                    throw new KorvoError(PORTAL_CONFIG_MISSING, 2, { message: 'Thread creation response missing id' });
                }
                meta.threadId = threadId;
                this.state.session!.metadata = meta;
            }
            const portalApiBase = safePortalBase(this.config.provider.apiEndpoint);
            const response = await fetch(`${portalApiBase}/threads/${meta.threadId}/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    role: 'user',
                    content: userMessage.content,
                    clientMessageId: userMessage.id,
                    context,
                }),
            });
            if (!response.ok) {
                throw new KorvoError(PORTAL_CONFIG_MISSING, 3, {
                  message: `Failed to send message: ${response.status}`,
                  status: response.status,
                });
            }
            if (response.body) {
                void this.handleStreamFromMessagesAPI(response.body);
            }
        }
        catch (err: unknown) {
            const errNormalized = normalizeError(err);
            const errorMessage = errNormalized.message;
            const chatError = this.setKorvoChatError(ERR_CHAT_500, CHAT_INSTANCE_SEND_THROW_ID, errorMessage);
            this.config.onError?.(chatError);
        }
        finally {
            // Only mark request complete; streaming state is controlled by stream handlers
            this.setState({ isLoading: false });
        }
    }
    private async createNewThreadAndRetry() {
        try {
            const tc = this.threadsClient!;
            const created = await tc.createThread({
                packageName: 'chat',
                systemName: 'general-chat',
                title: this.state.session?.title || `Chat ${Date.now()}`,
            });
            const threadId = (created as {
                thread?: {
                    id?: string;
                };
            }).thread?.id;
            if (!threadId) {
                throw new KorvoError(PORTAL_CONFIG_MISSING, 4, { message: 'Thread creation response missing id' });
            }
            if (this.state.session) {
                this.state.session.metadata = { ...this.state.session.metadata, threadId };
            }
            this.openStream(threadId);
        }
        catch (err: unknown) {
            const errNormalized = normalizeError(err);
            console.error(`[ChatInstance ${this.instanceId}] Failed to create new thread:`, errNormalized);
            this.setKorvoChatError(ERR_CHAT_500, CHAT_INSTANCE_THREAD_CREATE_THROW_ID, 'Failed to create new thread');
        }
    }
    public openStream(threadId: string | null) {
        // Maintain current thread reference; streaming is handled via fetch response body.
        this.currentThreadId = threadId;
    }
    private async handleStreamFromMessagesAPI(stream: ReadableStream<Uint8Array>) {
        try {
            const reader = stream.getReader();
            const decoder = new TextDecoder();
            let currentAssistantMessage: ChatMessage | null = null;
            // Enter streaming state as soon as we start consuming the stream
            this.setState({ isStreaming: true });
            while (true) {
                 
                const { done, value } = await reader.read();
                if (done)
                    break;
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        // Handle OpenAI-style stream terminator
                        if (line.trim() === 'data: [DONE]') {
                            if (currentAssistantMessage) {
                                this.updateMessage(currentAssistantMessage.id, { content: currentAssistantMessage.content });
                                currentAssistantMessage = null;
                            }
                            this.setState({ isStreaming: false });
                            continue;
                        }
                        try {
                            const payload = line.slice(6).trim();
                            if (!payload) {
                                continue;
                            }
                            // Some providers may emit non-JSON diagnostic lines; skip those safely
                            if (payload === '[DONE]') {
                                this.setState({ isStreaming: false });
                                continue;
                            }
                            if (!looksLikeJsonObjectPayload(payload)) {
                                continue;
                            }
                            const chunkData = parseStreamChunk(payload);
                            if (!chunkData) {
                                continue;
                            }
                            if (chunkData.type === 'content') {
                                if (!currentAssistantMessage) {
                                    // Check if there's already an assistant message that might be from this stream
                                    // Look for the most recent assistant message that was created recently (within last 5 seconds)
                                    // and either has no content or very little content (likely still streaming)
                                    const now = Date.now();
                                    const recentThreshold = STREAM_RECENT_THRESHOLD_MS;
                                    const existingAssistant = this.state.messages
                                        .slice()
                                        .reverse()
                                        .find(m => m.role === 'assistant' &&
                                        m.timestamp &&
                                        (now - m.timestamp) < recentThreshold &&
                                        (!m.content || m.content.trim().length < 50) // Empty or just started
                                    );
                                    if (existingAssistant) {
                                        // Reuse existing assistant message
                                        currentAssistantMessage = existingAssistant;
                                    }
                                    else {
                                        // Create new assistant message only if none exists
                                        currentAssistantMessage = {
                                            id: `assistant_${Date.now()}`,
                                            role: 'assistant',
                                            content: '',
                                            timestamp: Date.now()
                                        };
                                        this.addMessage(currentAssistantMessage);
                                    }
                                }
                                currentAssistantMessage.content += chunkData.content;
                                this.updateMessage(currentAssistantMessage.id, { content: currentAssistantMessage.content });
                                this.setState({ isStreaming: true });
                            }
                            else if (chunkData.type === 'tool_call_message') {
                                // Add tool call message via shared helper
                                console.log('🔧 [ChatInstance] Received tool_call_message event:', {
                                    toolCallId: chunkData.toolCall.id,
                                    toolName: chunkData.toolCall.name,
                                    timestamp: Date.now()
                                });
                                const tc: ToolCall = {
                                    id: chunkData.toolCall.id ?? `tc-${Date.now()}`,
                                    name: chunkData.toolCall.name ?? chunkData.toolCall.function?.name ?? 'unknown',
                                    arguments: (typeof chunkData.toolCall.arguments === 'object' && chunkData.toolCall.arguments !== null) ? chunkData.toolCall.arguments as Record<string, unknown> : {},
                                };
                                const toolCallMessage = composeToolExecutionMessage(tc);
                                console.log('🔧 [ChatInstance] Adding tool message to state:', toolCallMessage.id);
                                this.addMessage(toolCallMessage);
                            }
                            else if (chunkData.type === 'tool_call_update') {
                                // Update existing tool call message
                                const existingMessage = this.state.messages.find(m => m.role === 'tool_execution' &&
                                    m.toolCalls?.some(tc => tc.id === chunkData.toolCall.id));
                                if (existingMessage) {
                                    const tc: ToolCall = {
                                        id: chunkData.toolCall.id ?? 'unknown',
                                        name: chunkData.toolCall.name ?? chunkData.toolCall.function?.name ?? 'unknown',
                                        arguments: (typeof chunkData.toolCall.arguments === 'object' && chunkData.toolCall.arguments !== null) ? chunkData.toolCall.arguments as Record<string, unknown> : {},
                                    };
                                    const updates = applyToolUpdateToMessage(existingMessage, tc);
                                    this.updateMessage(existingMessage.id, updates);
                                }
                            }
                            else if (chunkData.type === 'tool_call_error') {
                                // Handle tool execution error
                                const existingMessage = this.state.messages.find(m => m.role === 'tool_execution' &&
                                    m.toolCalls?.some(tc => tc.id === chunkData.callId));
                                if (existingMessage) {
                                    const errorToolCall = existingMessage.toolCalls?.find(tc => tc.id === chunkData.callId);
                                    if (errorToolCall) {
                                        const modifiedToolCall = {
                                            ...errorToolCall,
                                            success: false,
                                            error: chunkData.error,
                                        };
                                        const updates = applyToolUpdateToMessage(existingMessage, modifiedToolCall);
                                        this.updateMessage(existingMessage.id, updates);
                                    }
                                }
                            }
                            else if (chunkData.type === 'done') {
                                if (currentAssistantMessage) {
                                    this.updateMessage(currentAssistantMessage.id, { content: currentAssistantMessage.content });
                                    currentAssistantMessage = null;
                                }
                                this.setState({ isStreaming: false });
                            }
                            else if (chunkData.type === 'error') {
                                this.setKorvoChatError(
                                    ERR_CHAT_500,
                                    CHAT_INSTANCE_STREAM_THROW_ID,
                                    chunkData.error || 'Stream error',
                                );
                                this.setState({ isStreaming: false });
                            }
                        }
                        catch (parseError: unknown) {
                            const parseErrorNormalized = normalizeError(parseError);
                            console.error(`[ChatInstance] Error parsing stream data:`, parseErrorNormalized);
                        }
                    }
                }
            }
        }
        catch (err: unknown) {
            const errNormalized = normalizeError(err);
            console.error(`[ChatInstance] Error handling stream:`, errNormalized);
            this.setKorvoChatError(ERR_CHAT_500, CHAT_INSTANCE_STREAM_HANDLER_THROW_ID, 'Failed to handle stream');
        }
    }
    private addMessage(message: ChatMessage) {
        // Deduplicate by message ID to prevent duplicate messages
        const existingIndex = this.state.messages.findIndex(m => m.id === message.id);
        if (existingIndex !== -1) {
            // Update existing message instead of adding duplicate
            this.state.messages[existingIndex] = message;
            this.emit('message:updated', message);
            return;
        }
        this.state.messages.push(message);
        this.emit('message:added', message);
    }
    private updateMessage(messageId: string, updates: Partial<ChatMessage>) {
        const messageIndex = this.state.messages.findIndex(m => m.id === messageId);
        if (messageIndex !== -1) {
            this.state.messages[messageIndex] = { ...this.state.messages[messageIndex], ...updates };
            this.emit('message:updated', this.state.messages[messageIndex]);
        }
    }
    private setKorvoChatError(baseCode: string, uniqueThrowId: number, message: string): ChatError {
        const korvo = new KorvoError(baseCode, uniqueThrowId, { message, status: 500 });
        const chatError: ChatError = {
            code: baseCode,
            message,
            korvoId: korvo.id,
            timestamp: Date.now(),
        };
        this.setError(chatError);
        return chatError;
    }
    private setError(error: ChatError) {
        this.state.error = error;
        this.emit('error:occurred', error);
        this.emit('state:changed', this.getState());
    }
    /** Clear the global error banner (e.g. user dismissed). */
    clearError(): void {
        if (this.state.error === null) {
            return;
        }
        this.state.error = null;
        this.setState({ error: null });
    }
    private setState(updates: Partial<ChatInstanceState>) {
        Object.assign(this.state, updates);
        this.emit('state:changed', this.getState());
    }
    pause() {
        this.isPaused = true;
        this.state.isPaused = true;
        this.emit('instance:paused');
        this.emit('state:changed', this.getState());
        // Don't close the stream when pausing - keep it alive for background streaming
        // Persist paused state
    }
    resume() {
        this.isPaused = false;
        this.state.isPaused = false;
        this.emit('instance:resumed');
        this.emit('state:changed', this.getState());
        // If we have a current thread, ensure stream is open
        if (this.currentThreadId && this.threadsClient) {
            this.openStream(this.currentThreadId);
        }
        // Persist resumed state
    }
    // Recover state after browser close/reopen
    async recoverState() {
        // Session recovery is now handled in initialize() via localStorage
        // Portal SDK handles server-side persistence
    }
    private startHeartbeat() {
        // Heartbeat every 30 seconds to maintain connection and detect issues
        this.heartbeatInterval = setInterval(() => {
            if (this.isDestroyed) {
                this.stopHeartbeat();
                return;
            }
            try {
                // Update last activity
                this.lastActivity = Date.now();
                // Portal SDK handles server persistence automatically
                // No local persistence needed
            }
            catch (err: unknown) {
                const errNormalized = normalizeError(err);
                console.warn(`[ChatInstance ${this.instanceId}] Heartbeat failed:`, errNormalized);
            }
        }, HEARTBEAT_INTERVAL_MS);
    }
    private stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }
    destroy() {
        this.isDestroyed = true;
        this.state.isDestroyed = true;
        this.stopHeartbeat();
        // Close the stream and clean up
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
        this.currentThreadId = null;
        this.removeAllListeners();
        this.emit('instance:destroyed');
    }
    clearMessages() {
        this.state.messages = [];
        this.emit('state:changed', this.getState());
    }
    retryLastMessage() {
        if (this.state.messages.length === 0)
            return;
        // Find the last user message
        const lastUserIndex = this.state.messages.map((msg, idx) => ({ msg, idx }))
            .reverse()
            .find(({ msg }) => msg.role === 'user');
        if (!lastUserIndex)
            return;
        // Remove all messages after the last user message (including any assistant responses)
        this.state.messages = this.state.messages.slice(0, lastUserIndex.idx + 1);
        // Clear any error state
        this.state.error = null;
        this.setState({ error: null });
        // Resend the last user message
        void this.sendMessage(lastUserIndex.msg.content, lastUserIndex.msg.files?.map(f => new File([], f.name, { type: f.type })) || []);
    }
}
